import axios from "axios";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Back from "../../components/button/back";

const AddBerita = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { ID } = location.state || {};

  /* =========================
     STATE
  ========================= */
  const [formData, setFormData] = useState({
    judul: "",
    deskripsi: "",
    tanggal: "",
    link: [],
  });

  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [existingMedia, setExistingMedia] = useState([]);
  const [removeImages, setRemoveImages] = useState([]);
  const [removeLink, setRemoveLink] = useState([]);

  /* =========================
     FETCH (EDIT MODE)
  ========================= */
  useEffect(() => {
    if (!ID) return;

    const fetchBerita = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}api/berita/${ID}`,
        );

        const berita = res.data;

        setFormData({
          judul: berita.judul || "",
          deskripsi: berita.deskripsi || "",
          tanggal: berita.tanggal
            ? new Date(berita.tanggal).toISOString().split("T")[0]
            : "",
          link: berita.link || [],
        });

        setExistingMedia(berita.media || berita.gambar || []);
      } catch (err) {
        console.error("Gagal memuat berita:", err);
      }
    };

    fetchBerita();
  }, [ID]);

  /* =========================
     HANDLE INPUT
  ========================= */
  const handleChange = (e) => {
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  /* =========================
     FILE BARU
  ========================= */
  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    setFiles((p) => [...p, ...selected]);
    setPreviews((p) => [...p, ...selected.map((f) => URL.createObjectURL(f))]);
    e.target.value = null;
  };

  const removeNewMedia = (index) => {
    setFiles((p) => p.filter((_, i) => i !== index));
    setPreviews((p) => p.filter((_, i) => i !== index));
  };

  /* =========================
     MEDIA LAMA
  ========================= */
  const deleteExistingMedia = (fileId) => {
    if (!window.confirm("Hapus gambar ini?")) return;
    setRemoveImages((p) => [...p, fileId]);
    setExistingMedia((p) => p.filter((m) => m.fileId !== fileId));
  };

  /* =========================
     LINK
  ========================= */
  const addLink = () => setFormData((p) => ({ ...p, link: [...p.link, ""] }));

  const updateLink = (i, val) => {
    const link = [...formData.link];
    link[i] = val;
    setFormData((p) => ({ ...p, link }));
  };

  const deleteLink = (i) => {
    setRemoveLink((p) => [...p, formData.link[i]]);
    setFormData((p) => ({
      ...p,
      link: p.link.filter((_, idx) => idx !== i),
    }));
  };

  /* =========================
     BUILD FORM DATA
  ========================= */
  const buildFormData = (isUpdate = false) => {
    const fd = new FormData();

    fd.append("judul", formData.judul);
    fd.append("deskripsi", formData.deskripsi);
    fd.append("tanggal", formData.tanggal);

    if (isUpdate) {
      // UPDATE
      formData.link.forEach((l) => l && fd.append("addLink", l));
      removeLink.forEach((l) => fd.append("removeLink", l));
      removeImages.forEach((id) => fd.append("removeImages", id));
    } else {
      // CREATE
      formData.link.forEach((l) => l && fd.append("link", l));
    }

    files.forEach((f) => fd.append("media", f));

    return fd;
  };

  /* =========================
     CREATE
  ========================= */
  const createBerita = async () => {
    const fd = buildFormData(false);
    await axios.post(`${process.env.REACT_APP_API_URL}api/berita`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };
  /* =========================
     UPDATE
  ========================= */
  const updateBerita = async () => {
    const fd = buildFormData(true);
    await axios.put(`${process.env.REACT_APP_API_URL}api/berita/${ID}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };
  /* =========================
     SUBMIT DISPATCHER
  ========================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (ID) await updateBerita();
      else await createBerita();

      navigate("/admin/berita");
    } catch (err) {
      console.error("Gagal menyimpan berita:", err);
    }
  };

  /* =========================
     RENDER
  ========================= */
  return (
    <div className="bg-blue-200 min-h-screen p-4">
      <div className="max-w-4xl mx-auto bg-white rounded shadow p-6">
        <h2 className="text-2xl font-bold mb-4">
          {ID ? "Edit Berita" : "Tambah Berita"}
        </h2>

        <form onSubmit={handleSubmit}>
          <input
            name="judul"
            value={formData.judul}
            onChange={handleChange}
            placeholder="Judul"
            className="w-full border p-2 mb-3 rounded"
          />

          <input
            type="date"
            name="tanggal"
            value={formData.tanggal}
            onChange={handleChange}
            className="w-full border p-2 mb-3 rounded"
          />

          <textarea
            name="deskripsi"
            rows={4}
            value={formData.deskripsi}
            onChange={handleChange}
            placeholder="Deskripsi"
            className="w-full border p-2 mb-3 rounded"
          />

          <input type="file" multiple onChange={handleFileChange} />

          {/* Preview Baru */}
          <div className="flex gap-3 mt-3 flex-wrap">
            {previews.map((src, i) => (
              <div key={i} className="relative">
                <img src={src} className="w-28 h-28 rounded object-cover" />
                <button
                  type="button"
                  onClick={() => removeNewMedia(i)}
                  className="absolute -top-2 -right-2 bg-red-600 text-white w-6 h-6 rounded-full"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          {/* Media Lama */}
          {existingMedia.length > 0 && (
            <div className="mt-4">
              <p className="font-semibold">Gambar Lama</p>
              <div className="flex gap-3 flex-wrap">
                {existingMedia.map((m) => (
                  <div key={m.fileId} className="relative">
                    <img
                      src={`${process.env.REACT_APP_API_URL}api/berita/media/${m.filename}`}
                      className="w-28 h-28 rounded object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => deleteExistingMedia(m.fileId)}
                      className="absolute -top-2 -right-2 bg-red-600 text-white w-6 h-6 rounded-full"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Link */}
          <div className="mt-4">
            <p className="font-semibold">Link</p>
            {formData.link.map((l, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input
                  value={l}
                  onChange={(e) => updateLink(i, e.target.value)}
                  className="flex-1 border p-2 rounded"
                />
                <button
                  type="button"
                  onClick={() => deleteLink(i)}
                  className="bg-red-500 text-white px-3 rounded"
                >
                  ✕
                </button>
              </div>
            ))}
            <button type="button" onClick={addLink} className="text-blue-600">
              + Tambah Link
            </button>
          </div>

          <button className="bg-blue-600 text-white px-4 py-2 rounded mt-4">
            {ID ? "Update" : "Simpan"}
          </button>
        </form>
      </div>

      <div className="flex justify-center mt-6">
        <Back onClick={() => navigate("/admin/berita")} />
      </div>
    </div>
  );
};

export default AddBerita;
